import type { AdapterCatalogItem, AdapterFieldDefinition, AgentAdapterConfig, Agent, AgentAdapterType, AgentExecutionConfig, AgentExecutionMode, AgentExecutionModeCatalogItem, AgentSharedConfig } from '@/types/api'

import { ApiError, apiClient } from '@/lib/api/client'
import {
  adapterCatalogListSchema,
  adapterFieldListSchema,
  agentAdapterConfigSchema,
  agentSharedConfigSchema,
  agentExecutionConfigSchema,
  agentExecutionModeCatalogListSchema,
  agentListSchema,
  agentSchema,
} from '@/lib/api/schemas'

function readPartialAgentCreateError(
  err: unknown,
): { agent: unknown; errors: Record<string, string> } | null {
  if (typeof err === 'object' && err !== null && 'status' in err && 'data' in err) {
    const legacy = err as {
      status?: number
      data?: { agent?: unknown; errors?: Record<string, string> }
    }
    if (legacy.status === 422 && legacy.data?.agent) {
      return { agent: legacy.data.agent, errors: legacy.data.errors ?? {} }
    }
  }
  if (err instanceof ApiError && err.status === 422 && err.body) {
    try {
      const data = JSON.parse(err.body) as {
        agent?: unknown
        errors?: Record<string, string>
        detail?: { agent?: unknown; errors?: Record<string, string> }
      }
      const agent = data.agent ?? data.detail?.agent
      if (agent) {
        return {
          agent,
          errors: data.errors ?? data.detail?.errors ?? {},
        }
      }
    } catch {
      return null
    }
  }
  return null
}

export function listAgents(companyId: string): Promise<Agent[]> {
  return apiClient.requestValidated(
    `/companies/${encodeURIComponent(companyId)}/agents`,
    agentListSchema,
  )
}

export interface CreateAgentInput {
  name: string
  role: string
  reportsToId: string | null
  adapterType: AgentAdapterType
  tokenBudget: number
  systemPrompt?: string | null
  requiresApproval?: boolean
  platformUrl?: string | null
}

export function createAgent(
  companyId: string,
  input: CreateAgentInput,
): Promise<Agent> {
  return apiClient.requestValidated(
    `/companies/${encodeURIComponent(companyId)}/agents`,
    agentSchema,
    { method: 'POST', body: JSON.stringify(input) },
  )
}

export interface RoutineSpec {
  enabled: boolean
  name?: string
  cron?: string
  timezone?: string
  human?: string
}

export interface CreateAgentAtomicInput {
  name: string
  role: string
  adapterType: AgentAdapterType
  adapterId?: string | null
  modelId?: string | null
  reportsToId?: string | null
  tokenBudget: number
  systemPrompt?: string | null
  requiresApproval?: boolean
  platformUrl?: string | null
  specialtyId?: string | null
  specialtyConfigValues?: Record<string, unknown>
  adapterFieldValues?: Record<string, unknown>
  executionMode?: AgentExecutionMode
  cronExpression?: string | null
  timezone?: string
  functionUrl?: string | null
  triggerConfig?: Record<string, unknown>
  isActive?: boolean
  routine?: RoutineSpec | null
}

export async function createAgentAtomic(
  companyId: string,
  input: CreateAgentAtomicInput,
): Promise<Agent> {
  try {
    return await apiClient.requestValidated(
      `/companies/${encodeURIComponent(companyId)}/agents`,
      agentSchema,
      { method: 'POST', body: JSON.stringify(input) },
    )
  } catch (err: unknown) {
    // 422 com partial success: backend criou o agente mas etapas secundárias falharam
    const partial = readPartialAgentCreateError(err)
    if (partial) {
      const parsed = agentSchema.safeParse(partial.agent)
      if (parsed.success) {
        const partialErr = new Error(
          `Agente criado com falhas parciais: ${Object.keys(partial.errors).join(', ')}`,
        ) as Error & { agent: Agent; partialErrors: Record<string, string> }
        partialErr.agent = parsed.data
        partialErr.partialErrors = partial.errors
        throw partialErr
      }
    }
    throw err
  }
}

export type UpdateAgentInput = Partial<
  Pick<
    Agent,
    | 'name'
    | 'role'
    | 'tokenBudget'
    | 'reportsToId'
    | 'systemPrompt'
    | 'requiresApproval'
    | 'platformUrl'
  >
>

export function updateAgent(
  agentId: string,
  patch: UpdateAgentInput,
): Promise<Agent> {
  return apiClient.requestValidated(
    `/agents/${encodeURIComponent(agentId)}`,
    agentSchema,
    { method: 'PATCH', body: JSON.stringify(patch) },
  )
}

export function pauseAgent(agentId: string): Promise<Agent> {
  return apiClient.requestValidated(
    `/agents/${encodeURIComponent(agentId)}/pause`,
    agentSchema,
    { method: 'POST' },
  )
}

export function resumeAgent(agentId: string): Promise<Agent> {
  return apiClient.requestValidated(
    `/agents/${encodeURIComponent(agentId)}/resume`,
    agentSchema,
    { method: 'POST' },
  )
}

export function killAgent(agentId: string): Promise<Agent> {
  return apiClient.requestValidated(
    `/agents/${encodeURIComponent(agentId)}/kill`,
    agentSchema,
    { method: 'POST' },
  )
}

export function getAgentExecutionConfig(
  agentId: string,
): Promise<AgentExecutionConfig> {
  return apiClient.requestValidated(
    `/agents/${encodeURIComponent(agentId)}/execution-config`,
    agentExecutionConfigSchema,
  )
}

export interface AgentExecutionSetupInput {
  executionMode: AgentExecutionMode
  triggerConfig: Record<string, unknown>
  functionUrl: string | null
  authSecretRef: string | null
  authHeaderName: string | null
  isActive: boolean
}

export function putAgentExecutionSetup(
  agentId: string,
  input: AgentExecutionSetupInput,
): Promise<AgentExecutionConfig> {
  return apiClient.requestValidated(
    `/agents/${encodeURIComponent(agentId)}/execution-setup`,
    agentExecutionConfigSchema,
    { method: 'PUT', body: JSON.stringify(input) },
  )
}

// -----------------------------------------------------------------------------
// Execution mode catalog (catalog-driven — substitui union hardcoded)
// -----------------------------------------------------------------------------

export function listAgentExecutionModes(): Promise<AgentExecutionModeCatalogItem[]> {
  return apiClient.requestValidated(
    `/agent-execution-modes`,
    agentExecutionModeCatalogListSchema,
  )
}

// -----------------------------------------------------------------------------
// Adapter Catalog (VEC-235)
// -----------------------------------------------------------------------------

export function listAdapters(companyId: string): Promise<AdapterCatalogItem[]> {
  return apiClient.requestValidated(
    `/companies/${encodeURIComponent(companyId)}/adapters`,
    adapterCatalogListSchema,
  )
}

export type AdapterFieldScope = 'company' | 'agent' | 'both'

export function listAdapterFields(
  adapterId: string,
  scope?: AdapterFieldScope,
): Promise<AdapterFieldDefinition[]> {
  const qs = scope ? `?scope=${encodeURIComponent(scope)}` : ''
  return apiClient.requestValidated(
    `/adapters/${encodeURIComponent(adapterId)}/fields${qs}`,
    adapterFieldListSchema,
  )
}

export function getAgentAdapterConfig(agentId: string): Promise<AgentAdapterConfig | null> {
  return apiClient.requestValidated(
    `/agents/${encodeURIComponent(agentId)}/adapter-config`,
    agentAdapterConfigSchema.nullable(),
  )
}

export interface SaveAgentAdapterConfigInput {
  adapterId: string
  fieldValuesJson: Record<string, unknown>
}

export function saveAgentAdapterConfig(
  agentId: string,
  input: SaveAgentAdapterConfigInput,
): Promise<AgentAdapterConfig> {
  return apiClient.requestValidated(
    `/agents/${encodeURIComponent(agentId)}/adapter-config`,
    agentAdapterConfigSchema,
    { method: 'PUT', body: JSON.stringify(input) },
  )
}

export function getAgentSharedConfig(agentId: string): Promise<AgentSharedConfig> {
  return apiClient.requestValidated(
    `/agents/${encodeURIComponent(agentId)}/shared-config`,
    agentSharedConfigSchema,
  )
}

export interface SaveAgentSharedConfigInput {
  values: Record<string, unknown>
}

export function saveAgentSharedConfig(
  agentId: string,
  input: SaveAgentSharedConfigInput,
): Promise<AgentSharedConfig> {
  return apiClient.requestValidated(
    `/agents/${encodeURIComponent(agentId)}/shared-config`,
    agentSharedConfigSchema,
    { method: 'PUT', body: JSON.stringify(input) },
  )
}

// -----------------------------------------------------------------------------
// Company Secrets (Vault)
// -----------------------------------------------------------------------------

export type CompanySecret = {
  id: string
  name: string
  description: string | null
  createdAt: string
  updatedAt: string
}

export async function listCompanySecrets(companyId: string): Promise<CompanySecret[]> {
  const raw = await apiClient.request<unknown>(
    `/companies/${encodeURIComponent(companyId)}/secrets`,
  )
  if (!Array.isArray(raw)) return []
  return raw as CompanySecret[]
}

export interface UpsertCompanySecretInput {
  name: string
  value: string
  description?: string
}

export async function upsertCompanySecret(
  companyId: string,
  input: UpsertCompanySecretInput,
): Promise<{ name: string; vaultSecretId: string }> {
  const raw = await apiClient.request<{ name: string; vaultSecretId: string }>(
    `/companies/${encodeURIComponent(companyId)}/secrets`,
    { method: 'POST', body: JSON.stringify(input) },
  )
  return raw
}

// -----------------------------------------------------------------------------
// Inbox (VEC-301)
// -----------------------------------------------------------------------------

export type EmailCategory = 'urgente' | 'ação' | 'informativo' | 'spam'

export type InboxEmail = {
  id: string
  from: string
  subject: string
  excerpt: string
  receivedAt: string
  category: EmailCategory
  isQuote: boolean
  isSpam: boolean
}

export async function getAgentInbox(agentId: string, limit = 20): Promise<InboxEmail[]> {
  const raw = await apiClient.request<unknown>(
    `/agents/${encodeURIComponent(agentId)}/inbox?limit=${limit}`,
  )
  if (!Array.isArray(raw)) return []
  return raw as InboxEmail[]
}
